import React, { useState, useLayoutEffect, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { C } from './primitives/constants';

/**
 * GuidedTooltip — Spotlight + tooltip for guided tours.
 *
 * Renders a full-screen backdrop with a cutout highlight around the target
 * element and a positioned tooltip card with step counter and navigation.
 *
 * Uses ReactDOM.createPortal to render into document.body.
 */
const GuidedTooltip = ({
  targetSelector,
  title,
  body,
  step,
  totalSteps,
  placement = 'bottom',
  onNext,
  onDismiss,
  showArrow = true,
}) => {
  const [targetRect, setTargetRect] = useState(null);
  const [visible, setVisible] = useState(false);

  const measure = useCallback(() => {
    if (!targetSelector) return;
    try {
      const el = document.querySelector(targetSelector);
      if (el) {
        setTargetRect(el.getBoundingClientRect());
      } else {
        setTargetRect(null);
      }
    } catch {
      setTargetRect(null);
    }
  }, [targetSelector]);

  // Measure target position
  useLayoutEffect(() => {
    measure();
  }, [measure]);

  // Scroll + resize listeners
  useEffect(() => {
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [measure]);

  // Entrance animation — fade in after mount
  useEffect(() => {
    if (targetRect) {
      const id = requestAnimationFrame(() => setVisible(true));
      return () => cancelAnimationFrame(id);
    }
    setVisible(false);
  }, [targetRect]);

  if (!targetRect) return null;

  const isLast = step === totalSteps;
  const pad = 4; // highlight padding around target

  // Calculate tooltip position with auto-flip to stay in viewport
  const gap = 12;
  const viewportPad = 12; // minimum padding from viewport edges
  const tooltipHeight = 160; // approximate tooltip height (increased for safety)
  const tooltipWidth = 300;
  const tooltipStyle = { position: 'fixed', zIndex: 10000, maxWidth: tooltipWidth };

  // Determine if bottom placement would overflow
  const wouldOverflowBottom = targetRect.bottom + gap + tooltipHeight > window.innerHeight;
  const wouldOverflowTop = targetRect.top - gap - tooltipHeight < 0;

  // Resolve placement — auto-flip vertical if it would overflow
  let resolvedPlacement = placement;
  if (placement === 'bottom' && wouldOverflowBottom && !wouldOverflowTop) {
    resolvedPlacement = 'top';
  } else if (placement === 'top' && wouldOverflowTop && !wouldOverflowBottom) {
    resolvedPlacement = 'bottom';
  }

  // Helper: clamp a value between min and max
  const clamp = (val, min, max) => Math.max(min, Math.min(max, val));

  switch (resolvedPlacement) {
    case 'top':
      tooltipStyle.bottom = window.innerHeight - targetRect.top + gap;
      tooltipStyle.left = clamp(
        targetRect.left + targetRect.width / 2 - tooltipWidth / 2,
        viewportPad,
        window.innerWidth - tooltipWidth - viewportPad
      );
      tooltipStyle.transform = `translateY(${visible ? '0' : '8px'})`;
      break;
    case 'left': {
      const leftTop = clamp(
        targetRect.top + targetRect.height / 2 - tooltipHeight / 2,
        viewportPad,
        window.innerHeight - tooltipHeight - viewportPad
      );
      tooltipStyle.right = window.innerWidth - targetRect.left + gap;
      tooltipStyle.top = leftTop;
      tooltipStyle.transform = `translateX(${visible ? '0' : '8px'})`;
      break;
    }
    case 'right': {
      const rightTop = clamp(
        targetRect.top + targetRect.height / 2 - tooltipHeight / 2,
        viewportPad,
        window.innerHeight - tooltipHeight - viewportPad
      );
      tooltipStyle.left = targetRect.right + gap;
      tooltipStyle.top = rightTop;
      tooltipStyle.transform = `translateX(${visible ? '0' : '-8px'})`;
      break;
    }
    case 'bottom':
    default:
      tooltipStyle.top = targetRect.bottom + gap;
      tooltipStyle.left = clamp(
        targetRect.left + targetRect.width / 2 - tooltipWidth / 2,
        viewportPad,
        window.innerWidth - tooltipWidth - viewportPad
      );
      tooltipStyle.transform = `translateY(${visible ? '0' : '-8px'})`;
      break;
  }

  tooltipStyle.opacity = visible ? 1 : 0;
  tooltipStyle.transition = 'opacity 0.25s ease, transform 0.25s ease';

  // Arrow styles — compute arrow position relative to target center
  const arrowSize = 6;
  const arrowBase = {
    position: 'absolute',
    width: 0,
    height: 0,
    borderStyle: 'solid',
  };

  // For left/right: arrow points at target's vertical center
  // For top/bottom: arrow points at target's horizontal center
  const targetCenterY = targetRect.top + targetRect.height / 2;
  const targetCenterX = targetRect.left + targetRect.width / 2;

  // Compute arrow offsets relative to tooltip's actual position
  const arrowTopForLR = tooltipStyle.top != null
    ? clamp(targetCenterY - tooltipStyle.top, 16, tooltipHeight - 16)
    : '50%';
  const arrowLeftForTB = tooltipStyle.left != null
    ? clamp(targetCenterX - tooltipStyle.left, 16, tooltipWidth - 16)
    : '50%';

  const arrowStyles = {
    top: {
      ...arrowBase,
      bottom: -arrowSize,
      left: typeof arrowLeftForTB === 'number' ? arrowLeftForTB : '50%',
      transform: typeof arrowLeftForTB === 'number' ? 'none' : 'translateX(-50%)',
      borderWidth: `${arrowSize}px ${arrowSize}px 0 ${arrowSize}px`,
      borderColor: `${C.s2} transparent transparent transparent`,
    },
    bottom: {
      ...arrowBase,
      top: -arrowSize,
      left: typeof arrowLeftForTB === 'number' ? arrowLeftForTB : '50%',
      transform: typeof arrowLeftForTB === 'number' ? 'none' : 'translateX(-50%)',
      borderWidth: `0 ${arrowSize}px ${arrowSize}px ${arrowSize}px`,
      borderColor: `transparent transparent ${C.s2} transparent`,
    },
    left: {
      ...arrowBase,
      right: -arrowSize,
      top: typeof arrowTopForLR === 'number' ? arrowTopForLR : '50%',
      transform: typeof arrowTopForLR === 'number' ? 'none' : 'translateY(-50%)',
      borderWidth: `${arrowSize}px 0 ${arrowSize}px ${arrowSize}px`,
      borderColor: `transparent transparent transparent ${C.s2}`,
    },
    right: {
      ...arrowBase,
      left: -arrowSize,
      top: typeof arrowTopForLR === 'number' ? arrowTopForLR : '50%',
      transform: typeof arrowTopForLR === 'number' ? 'none' : 'translateY(-50%)',
      borderWidth: `${arrowSize}px ${arrowSize}px ${arrowSize}px 0`,
      borderColor: `transparent ${C.s2} transparent transparent`,
    },
  };

  const content = (
    <>
      {/* Backdrop overlay */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.6)',
          zIndex: 9998,
        }}
        onClick={onDismiss}
      />

      {/* Cutout highlight over target */}
      <div
        style={{
          position: 'fixed',
          top: targetRect.top - pad,
          left: targetRect.left - pad,
          width: targetRect.width + pad * 2,
          height: targetRect.height + pad * 2,
          zIndex: 9999,
          boxShadow: '0 0 0 4px rgba(232,184,0,0.3)',
          borderRadius: 8,
          pointerEvents: 'none',
        }}
      />

      {/* Tooltip card */}
      <div style={tooltipStyle}>
        <div
          style={{
            position: 'relative',
            background: C.s2,
            border: '1px solid #E8B80033',
            borderRadius: 12,
            padding: 16,
            maxWidth: 300,
          }}
        >
          {/* Arrow */}
          {showArrow && <div style={arrowStyles[resolvedPlacement] || arrowStyles.bottom} />}

          {/* Title */}
          <div
            style={{
              color: C.gold,
              fontSize: 14,
              fontWeight: 700,
              marginBottom: 6,
            }}
          >
            {title}
          </div>

          {/* Body */}
          <div
            style={{
              color: C.t2,
              fontSize: 13,
              lineHeight: 1.5,
              marginBottom: 12,
            }}
          >
            {body}
          </div>

          {/* Footer: step counter + buttons */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            {/* Step counter */}
            <span style={{ color: C.t4, fontSize: 11 }}>
              {step} of {totalSteps}
            </span>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {/* Skip tour */}
              <button
                type="button"
                onClick={onDismiss}
                style={{
                  color: C.t3,
                  fontSize: 11,
                  cursor: 'pointer',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                }}
              >
                Skip tour
              </button>

              {/* Next / Got it */}
              <button
                onClick={onNext}
                style={{
                  background: C.gold,
                  color: '#000',
                  border: 'none',
                  borderRadius: 8,
                  fontSize: 13,
                  fontWeight: 600,
                  padding: '8px 20px',
                  cursor: 'pointer',
                }}
              >
                {isLast ? 'Got it' : 'Next'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  return ReactDOM.createPortal(content, document.body);
};

export default GuidedTooltip;
